/**
 * Прогресс цели, группировка по сферам и смена статуса.
 *
 * Расчёт прогресса вынесен из компонентов: одна и та же цифра показывается в
 * карточке, в детальной шторке и в сводке по сфере — она обязана считаться
 * одинаково во всех трёх местах.
 */

import { isHabitActive } from '@/entities/habit/model/habit-service';
import { type SphereId, SPHERE_IDS } from '@/shared/config/spheres';
import type { Goal, Habit, Task } from '@/shared/db/schema';
import { diffDays, type IsoDate, today } from '@/shared/lib/datetime';

/** Откуда взят прогресс — это влияет на подпись под индикатором. */
export type ProgressSource = 'tasks' | 'habits' | 'mixed' | 'manual' | 'none';

export interface GoalProgress {
  /** Доля 0…1. */
  value: number;
  source: ProgressSource;
  doneTasks: number;
  totalTasks: number;
  /** Сколько привычек цели дошли до своего срока. */
  doneHabits: number;
  totalHabits: number;
}

/**
 * База процента: «сколько шагов закрыто из скольких».
 *
 * Показывается вплотную к проценту и существует ровно для этого. Числа не
 * совпадают намеренно: «40 %» включает частичный зачёт привычки, дошедшей до
 * 17-го дня из 21, а «0 из 2» считает только полностью закрытые шаги. Пока
 * второе число отсутствовало, первое выглядело ошибкой — 40 % рядом с «0 из 1»
 * не складывались ни во что.
 *
 * `null` — у цели нет ни задач, ни привычек: процент задан вручную, и базы у
 * него не существует.
 */
export function goalSteps(progress: GoalProgress): { done: number; total: number } | null {
  const total = progress.totalTasks + progress.totalHabits;
  if (total === 0) return null;

  return { done: progress.doneTasks + progress.doneHabits, total };
}

const EMPTY: GoalProgress = {
  value: 0,
  source: 'none',
  doneTasks: 0,
  totalTasks: 0,
  doneHabits: 0,
  totalHabits: 0,
};

/**
 * Прогресс цели.
 *
 * Цель складывается из двух вещей: разовых шагов и привычек. Задача либо
 * закрыта, либо нет; привычка засчитывается по мере приближения к своему сроку
 * («21 день из 21»). Поэтому знаменатель — все шаги и все привычки вместе, а
 * числитель — закрытые задачи плюс доли привычек. Из этого следует главное:
 * цель доходит до 100% только когда сделаны все задачи **и** выдержаны все
 * привычки.
 *
 * Считать привычку одним «шагом» наравне с задачей — сознательное упрощение.
 * Взвешивать их было бы честнее в теории, но вес пришлось бы откуда-то взять,
 * а любой выбранный коэффициент оказался бы произвольным и непонятным.
 *
 * Ручной процент остаётся для целей, которые невозможно разбить («держать
 * вес»), и для тех, кто ещё не завёл ни задач, ни привычек. Отменённые задачи
 * из знаменателя исключаются: от них уже отказались, и держать их в расчёте
 * значило бы вечно занижать прогресс.
 */
export function goalProgress(
  goal: Goal,
  tasks: readonly Task[],
  /** Доли выполнения привычек цели, 0…1 каждая. */
  habitShares: readonly number[] = [],
): GoalProgress {
  if (goal.status === 'achieved') {
    return { ...EMPTY, value: 1, source: 'manual' };
  }

  const relevant = tasks.filter((task) => task.status !== 'cancelled');
  const doneTasks = relevant.filter((task) => task.status === 'done').length;
  const doneHabits = habitShares.filter((share) => share >= 1).length;

  const parts = relevant.length + habitShares.length;

  if (parts > 0) {
    const earned = doneTasks + habitShares.reduce((sum, share) => sum + Math.min(1, share), 0);

    return {
      value: Math.min(1, earned / parts),
      source:
        relevant.length === 0 ? 'habits' : habitShares.length === 0 ? 'tasks' : 'mixed',
      doneTasks,
      totalTasks: relevant.length,
      doneHabits,
      totalHabits: habitShares.length,
    };
  }

  if (goal.manualProgress !== null) {
    return {
      ...EMPTY,
      value: Math.max(0, Math.min(100, goal.manualProgress)) / 100,
      source: 'manual',
    };
  }

  return EMPTY;
}

/**
 * Цели, которые пора отметить достигнутыми.
 *
 * Сама цель статус не меняет — это делает экран; здесь только правило, и оно
 * вынесено отдельно, чтобы его можно было проверить без базы и React.
 *
 * Берутся только активные цели: пауза и архив — осознанные состояния, и
 * выводить из них расчётом неправильно. Достигнутые пропускаются сами собой,
 * иначе отметка возвращалась бы каждый раз, как человек вернул цель в работу.
 */
export function goalsReadyToAchieve(
  goals: readonly Goal[],
  progressOf: (goal: Goal) => GoalProgress,
): Goal[] {
  return goals.filter((goal) => {
    if (goal.status !== 'active') return false;

    const progress = progressOf(goal);
    // Цель без единого шага и без ручной оценки не «достигнута», а просто
    // пуста: у неё нечему выполняться.
    if (progress.source === 'none') return false;

    return progress.value >= 1;
  });
}

/** Срок горит: до цели меньше двух недель либо он уже прошёл. */
export function isDeadlineNear(goal: Goal, reference: IsoDate = today()): boolean {
  if (goal.targetDate === null || goal.status !== 'active') return false;
  return diffDays(reference, goal.targetDate) <= 14;
}

/**
 * Порядок целей: сначала активные, внутри — с ближайшим сроком.
 * Бессрочные уходят в конец: у них нет внешнего давления, и они не должны
 * оттеснять то, что действительно горит.
 */
export function compareGoals(a: Goal, b: Goal): number {
  const statusWeight = { active: 0, paused: 1, achieved: 2, archived: 3 } as const;
  if (a.status !== b.status) return statusWeight[a.status] - statusWeight[b.status];

  if (a.targetDate !== b.targetDate) {
    if (a.targetDate === null) return 1;
    if (b.targetDate === null) return -1;
    return a.targetDate < b.targetDate ? -1 : 1;
  }

  return a.position - b.position;
}

export interface SphereGoals {
  sphereId: SphereId;
  goals: Goal[];
}

/**
 * Группировка целей по сферам в порядке колеса баланса.
 * Пустые сферы отбрасываются: пустой заголовок сферы не несёт информации,
 * а перекос по сферам виден на диаграмме выше.
 */
export function groupGoalsBySphere(goals: readonly Goal[]): SphereGoals[] {
  return SPHERE_IDS.map((sphereId) => ({
    sphereId,
    goals: goals.filter((goal) => goal.sphereId === sphereId).sort(compareGoals),
  })).filter((group) => group.goals.length > 0);
}

/**
 * Индекс «цель → её элементы».
 *
 * Один проход вместо фильтра по всему списку на каждую цель: карточки считали
 * прогресс независимо, и тридцать целей на двадцати тысячах задач давали
 * шестьсот тысяч итераций — на каждый рендер экрана.
 */
export function groupByGoal<T extends { goalId: string | null }>(
  items: readonly T[],
): Map<string, T[]> {
  const byGoal = new Map<string, T[]>();

  for (const item of items) {
    if (item.goalId === null) continue;

    const group = byGoal.get(item.goalId);
    if (group) group.push(item);
    else byGoal.set(item.goalId, [item]);
  }

  return byGoal;
}

/** Задачи и привычки, привязанные к цели. */
export function linkedToGoal<T extends { goalId: string | null }>(
  items: readonly T[],
  goalId: string,
): T[] {
  return items.filter((item) => item.goalId === goalId);
}

/**
 * Привычки цели, которые ещё в работе.
 *
 * Архивные исключаются: они не показываются в трекере, отмечать их нельзя, и
 * держать их в знаменателе прогресса значит навсегда запереть цель ниже 100%.
 */
export function activeHabitsOfGoal(habits: readonly Habit[], goalId: string): Habit[] {
  return linkedToGoal(habits, goalId).filter(isHabitActive);
}

/** Индекс «цель → сколько привычек» для карточек списка. */
export function countHabitsByGoal(habits: readonly Habit[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const habit of habits) {
    if (habit.goalId === null || !isHabitActive(habit)) continue;
    counts.set(habit.goalId, (counts.get(habit.goalId) ?? 0) + 1);
  }
  return counts;
}

export const GOAL_STATUS_LABEL: Record<Goal['status'], string> = {
  active: 'В работе',
  achieved: 'Достигнута',
  paused: 'На паузе',
  archived: 'В архиве',
};
